import { useState } from "react";
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];


function splitYearMonth(value: string): { year: number; month: number } | null {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(value);
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]) };
}

export default function XeroRetrievalMonthCell({
  draft,
  tenantName,
  isPending,
  onDraftChange,
}: {
  draft: string | null;
  tenantName: string;
  isPending: boolean;
  onDraftChange: (next: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = draft ? splitYearMonth(draft) : null;

  // The year the picker opens on: the drafted one, else this year.
  const [year, setYear] = useState(
    () => selected?.year ?? new Date().getFullYear(),
  );

  const pick = (next: string | null) => {
    setOpen(false);
    onDraftChange(next);
  };

  // Selected month index (0-11) for highlight comparison, matching MONTHS.
  const selectedMonthIndex = selected ? selected.month - 1 : undefined;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        // Reopening should start from the drafted value rather than wherever the
        // year stepper was left last time.
        if (next) setYear(selected?.year ?? new Date().getFullYear());
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={isPending}
          className="h-8 w-[150px] justify-start gap-2 font-normal"
          aria-label={`Retrieval month for ${tenantName}`}
        >
          <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
          {selected ? (
            `${MONTHS[selected.month - 1]} ${selected.year}`
          ) : (
            <span className="text-muted-foreground">Set month</span>
          )}
        </Button>
      </PopoverTrigger>
      {/* PopoverContent portals, so this is not clipped by the table. */}
      <PopoverContent align="start" className="w-auto p-0">
        <div className="flex items-center justify-between border-b border-border px-2 py-1.5">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setYear((y) => y - 1)}
            aria-label="Previous year"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium tabular-nums text-foreground">
            {year}
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setYear((y) => y + 1)}
            aria-label="Next year"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-3 gap-1 p-2">
          {MONTHS.map((label, index) => {
            const isSelected =
              selected?.year === year && selectedMonthIndex === index;
            return (
              <Button
                key={label}
                variant={isSelected ? "default" : "ghost"}
                size="sm"
                className="h-8 w-14 font-normal"
                onClick={() =>
                  pick(`${year}-${String(index + 1).padStart(2, "0")}`)
                }
              >
                {label}
              </Button>
            );
          })}
        </div>

        {draft && (
          <div className="border-t border-border p-2">
            {/* Re-clicking the selected month does not clear it, so the
                affordance is explicit. */}
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => pick(null)}
            >
              Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
